import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Building2, Plus, Briefcase, CheckCircle, AlertTriangle, XCircle, Search, MapPin, Calendar, Eye } from 'lucide-react';
import { useTheme } from '../../components/ui/theme-provider';
import { cn } from '../../lib/utils';
import { ROUTES } from '../../App';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useToast } from '../../hooks/use-toast';
import { format } from 'date-fns'; // Ensure date-fns is installed

const PracticeDashboardPage = ({ user, currentUser }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true); // Overall page loading
  const [verifiedPracticesCount, setVerifiedPracticesCount] = useState(0);
  const [pendingPracticesCount, setPendingPracticesCount] = useState(0);
  const [rejectedPracticesCount, setRejectedPracticesCount] = useState(0);
  const [jobListings, setJobListings] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [activeJobsOnly, setActiveJobsOnly] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Effect to count verified, pending, and rejected practices
  useEffect(() => {
    if (currentUser?.managedPractices) {
      let verified = 0;
      let pending = 0;
      let rejected = 0;
      currentUser.managedPractices.forEach(p => {
        if (p.verificationStatus === 'verified') verified++;
        else if (p.verificationStatus === 'pending') pending++;
        else if (p.verificationStatus === 'rejected') rejected++;
      });
      setVerifiedPracticesCount(verified);
      setPendingPracticesCount(pending);
      setRejectedPracticesCount(rejected);
    }
    setLoading(false); // Stop overall loading once practice counts are processed
  }, [currentUser?.managedPractices]);

  // Fetch job listings posted by this user
  useEffect(() => {
    const fetchJobListings = async () => {
      if (!user?.uid) return;
      
      try {
        setLoadingJobs(true);
        const jobsQuery = query(
          collection(db, 'job_listings'),
          where('postedByUid', '==', user.uid),
          orderBy('postedAt', 'desc')
        );
        
        const querySnapshot = await getDocs(jobsQuery);
        const jobs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setJobListings(jobs);
      } catch (error) {
        console.error('Error fetching job listings:', error);
        toast({
          title: "Error",
          description: "Failed to load job listings",
          variant: "destructive"
        });
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobListings();
  }, [user?.uid, toast]);

  // Filter jobs based on active state and search term
  const filteredJobs = useMemo(() => {
    return jobListings.filter(job => {
      // Check if job is expired
      const isExpired = job.expiresAt && new Date(job.expiresAt.toDate ? job.expiresAt.toDate() : job.expiresAt) < new Date();
      
      // Update job status to inactive if expired (this could be done in a background process)
      const effectiveStatus = isExpired ? 'inactive' : job.status;
      
      // First apply active/all filter
      if (activeJobsOnly && effectiveStatus !== 'active') {
        return false;
      }
      
      // Then apply search term filter if there is one
      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        return (
          job.title?.toLowerCase().includes(searchTermLower) || 
          job.practiceInfo?.name?.toLowerCase().includes(searchTermLower) ||
          job.employmentType?.toLowerCase().includes(searchTermLower)
        );
      }
      
      return true;
    });
  }, [jobListings, activeJobsOnly, searchTerm]);

  // Calculate counts for the job status summary
  const jobCounts = useMemo(() => {
    const now = new Date();
    const active = jobListings.filter(job => {
      const isExpired = job.expiresAt && new Date(job.expiresAt.toDate ? job.expiresAt.toDate() : job.expiresAt) < now;
      return !isExpired && job.status === 'active';
    }).length;
    
    const inactive = jobListings.filter(job => {
      const isExpired = job.expiresAt && new Date(job.expiresAt.toDate ? job.expiresAt.toDate() : job.expiresAt) < now;
      return isExpired || job.status === 'closed' || job.status === 'inactive';
    }).length;
    
    const total = jobListings.length;
    return { active, inactive, total };
  }, [jobListings]);

  // Determine if the "Getting Started" guide should be shown
  const showGettingStartedGuide = !(verifiedPracticesCount > 0 && jobListings.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // ADD THIS CONSOLE LOG
  console.log("[PracticeDashboardPage] Current theme:", theme);

  return (
    <div className="flex-1 space-y-5 p-5">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div>
          <h1 className={cn(
            "text-3xl font-bold tracking-tight",
            theme === 'light' ? 'text-purple-700' : 'text-white'
          )}>
            Practice Dashboard
          </h1>
          <p className={cn(
            "text-sm mt-1",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            Manage your practice verifications and job postings.
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left Column: Getting Started (conditional) & Practice Status */}
        <div className="lg:col-span-1 flex flex-col gap-5">
          {showGettingStartedGuide && (
            <Card className={cn(
              theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
            )}>
              <CardHeader>
                <CardTitle className={cn(
                  "text-lg",
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                )}>
                  Getting Started
                </CardTitle>
                <CardDescription className={cn(
                  "text-xs",
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Follow these steps to set up your practice account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className={`flex items-start gap-3 ${verifiedPracticesCount > 0 ? 'opacity-50' : ''}`}>
                  <div className={`mt-1 h-5 w-5 flex-shrink-0 rounded-full flex items-center justify-center ${verifiedPracticesCount > 0 ? 'bg-green-500' : 'bg-purple-600'} text-white text-xs`}>1</div>
                  <div>
                    <h4 className={cn("font-medium text-sm", theme === 'light' ? 'text-gray-800' : 'text-gray-100')}>Verify Your Practice(s)</h4>
                    <p className={cn("text-xs", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Add and submit your practice details for verification to ensure authenticity.
                    </p>
                    {verifiedPracticesCount === 0 && (
                       <Button size="sm" className="mt-2 bg-purple-600 hover:bg-purple-700 text-white text-xs" onClick={() => navigate(ROUTES.PRACTICE_VERIFY)}>
                         Verify Practices
                       </Button>
                    )}
                  </div>
                </div>
                <div className={`flex items-start gap-3 ${(verifiedPracticesCount > 0 && jobListings.length > 0) ? 'opacity-50' : (verifiedPracticesCount === 0 ? 'opacity-50' : '')}`}>
                  <div className={`mt-1 h-5 w-5 flex-shrink-0 rounded-full flex items-center justify-center ${(verifiedPracticesCount > 0 && jobListings.length > 0) ? 'bg-green-500' : (verifiedPracticesCount > 0 ? 'bg-purple-600' : 'bg-gray-400')} text-white text-xs`}>2</div>
                  <div>
                    <h4 className={cn("font-medium text-sm", theme === 'light' ? 'text-gray-800' : 'text-gray-100')}>Post Your First Job</h4>
                    <p className={cn("text-xs", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Once a practice is verified, you can post job listings.
                    </p>
                    {verifiedPracticesCount > 0 && jobListings.length === 0 && (
                      <Button size="sm" className="mt-2 bg-purple-600 hover:bg-purple-700 text-white text-xs" onClick={() => navigate(ROUTES.PRACTICE_JOB_NEW)}>
                        Post New Job
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3 opacity-50">
                  <div className="mt-1 h-5 w-5 flex-shrink-0 rounded-full bg-gray-400 text-white text-xs">3</div>
                  <div>
                    <h4 className={cn("font-medium text-sm", theme === 'light' ? 'text-gray-800' : 'text-gray-100')}>Manage Applications</h4>
                    <p className={cn("text-xs", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      (Coming Soon) Review and manage applications for your job postings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className={cn(
            theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
          )}>
            <CardHeader>
              <CardTitle className={cn(
                "text-lg",
                theme === 'light' ? 'text-gray-900' : 'text-white'
              )}>
                Practice Verification Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className={cn("text-sm", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Verified Practices</span>
                </div>
                <span className={cn("font-semibold", theme === 'light' ? 'text-gray-800' : 'text-white')}>{verifiedPracticesCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span className={cn("text-sm", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Pending Verification</span>
                </div>
                <span className={cn("font-semibold", theme === 'light' ? 'text-gray-800' : 'text-white')}>{pendingPracticesCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className={cn("text-sm", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Rejected Practices</span>
                </div>
                <span className={cn("font-semibold", theme === 'light' ? 'text-gray-800' : 'text-white')}>{rejectedPracticesCount}</span>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => navigate(ROUTES.PRACTICE_VERIFY)}
              >
                Manage My Practices
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Right Column: Job Postings Overview */}
        <div className="lg:col-span-2">
          <Card className={cn(
            "h-full flex flex-col",
            theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
          )}>
            <CardHeader className="pb-2">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <CardTitle className={cn(
                    "text-lg",
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  )}>
                    My Job Postings
                  </CardTitle>
                  <CardDescription className={cn(
                    "text-xs",
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  )}>
                    {jobCounts.total > 0 
                      ? `${jobCounts.active} active, ${jobCounts.inactive} inactive, ${jobCounts.total} total jobs`
                      : "You haven't posted any jobs yet."}
                  </CardDescription>
                </div>
                <div className="flex items-center">
                  <Button 
                    onClick={() => navigate(ROUTES.PRACTICE_JOB_NEW)}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={verifiedPracticesCount === 0} // Disable if no verified practices
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Post Job
                  </Button>
                </div>
              </div>
            </CardHeader>

            <div className={cn(
              "px-6 py-2 border-b",
              theme === 'light' ? 'border-gray-200' : 'border-gray-700'
            )}>
              <div className="flex flex-col md:flex-row items-center gap-3 justify-between">
                <div className="relative w-full md:w-64">
                  {/* Custom search input without search icon */}
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    className={cn(
                      "flex h-10 w-full rounded-md border px-3 py-2 text-sm",
                      "placeholder:text-gray-400",
                      "focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                      "appearance-none",
                      theme === 'light' 
                        ? 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400' 
                        : 'bg-[#1F1F2E] border-gray-700 text-white hover:bg-[#2A2A3E] hover:border-gray-600'
                    )}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-sm",
                    theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  )}>
                    Show:
                  </span>
                  <Select 
                    value={activeJobsOnly ? 'active' : 'all'} 
                    onValueChange={(value) => setActiveJobsOnly(value === 'active')}
                  >
                    <SelectTrigger className={cn(
                      "w-[120px]",
                      theme === 'light' 
                        ? 'bg-white border-gray-300 text-gray-900' 
                        : 'bg-[#1F1F2E] border-gray-700 text-white'
                    )}>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className={theme === 'light' ? 'bg-white text-gray-900' : 'bg-[#1F1F2E] text-white'}>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="all">All Jobs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <CardContent className="p-0 overflow-y-auto flex-1 flex flex-col">
              {loadingJobs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                </div>
              ) : filteredJobs.length === 0 ? (
                <div className="text-center py-12 px-6">
                  {jobListings.length === 0 ? (
                    <>
                      <Briefcase className={cn(
                        "h-12 w-12 mx-auto mb-4",
                        theme === 'light' ? 'text-gray-400' : 'text-gray-600'
                      )} />
                      <p className={cn(
                        "text-sm mb-4",
                        theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                      )}>
                        {verifiedPracticesCount > 0 
                          ? "You haven't posted any jobs yet."
                          : "Please verify a practice before posting jobs."}
                      </p>
                      <Button
                        onClick={() => {
                          if (verifiedPracticesCount > 0) {
                            navigate(ROUTES.PRACTICE_JOB_NEW);
                          } else {
                            navigate(ROUTES.PRACTICE_VERIFY);
                          }
                        }}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {verifiedPracticesCount > 0 ? "Post Your First Job" : "Verify a Practice"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className={cn(
                        "text-sm",
                        theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                      )}>
                        {searchTerm 
                          ? `No jobs found matching "${searchTerm}"` 
                          : `No ${activeJobsOnly ? 'active' : ''} jobs found.`}
                      </p>
                      {searchTerm && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSearchTerm('')}
                          className="mt-2"
                        >
                          Clear Search
                        </Button>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredJobs.map((job) => (
                    <div
                      key={job.id}
                      className={cn(
                        "p-4 cursor-pointer transition-colors",
                        theme === 'light' 
                          ? 'hover:bg-gray-50' 
                          : 'hover:bg-gray-800'
                      )}
                      onClick={() => navigate(`${ROUTES.PRACTICE_JOBS}/${job.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 pr-4">
                          <h4 className={cn(
                            "font-medium text-base truncate mb-1",
                            theme === 'light' ? 'text-gray-900' : 'text-white'
                          )}>
                            {job.title}
                          </h4>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5 text-gray-500" />
                              <span className={cn(
                                "text-xs",
                                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                              )}>
                                {job.practiceInfo?.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5 text-gray-500" />
                              <span className={cn(
                                "text-xs",
                                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                              )}>
                                Posted {job.postedAt?.toDate ? format(job.postedAt.toDate(), 'MMM d, yyyy') : 'Recently'}
                              </span>
                            </div>
                            {job.views > 0 && (
                              <div className="flex items-center gap-1">
                                <Eye className="h-3.5 w-3.5 text-gray-500" />
                                <span className={cn(
                                  "text-xs",
                                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                                )}>
                                  {job.views} view{job.views !== 1 ? 's' : ''}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {job.description && (
                            <p className={cn(
                              "text-xs line-clamp-1",
                              theme === 'light' ? 'text-gray-500' : 'text-gray-500'
                            )}>
                              {job.description.substring(0, 80)}
                              {job.description.length > 80 ? '...' : ''}
                            </p>
                          )}
                          
                          {/* Show expiration info */}
                          {job.expiresAt && (
                            <p className={cn(
                              "text-xs",
                              theme === 'light' ? 'text-gray-500' : 'text-gray-500'
                            )}>
                              Expires: {job.expiresAt.toDate ? format(job.expiresAt.toDate(), 'MMM d, yyyy') : format(new Date(job.expiresAt), 'MMM d, yyyy')}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge 
                            className={cn(
                              "capitalize text-xs px-2 py-0.5", // Ensure consistent padding and text size
                              job.employmentType === 'Full-time' ? (theme === 'light' ? 'bg-blue-100 text-blue-800' : 'bg-blue-900/30 text-blue-400') : 
                              job.employmentType === 'Part-time' ? (theme === 'light' ? 'bg-purple-100 text-purple-800' : 'bg-purple-900/30 text-purple-400') : 
                              job.employmentType === 'Locum' ? (theme === 'light' ? 'bg-teal-100 text-teal-800' : 'bg-teal-900/30 text-teal-400') : // Changed Locum color
                              (theme === 'light' ? 'bg-gray-100 text-gray-800' : 'bg-gray-800 text-gray-400')
                            )}
                          >
                            {job.employmentType || 'N/A'}
                          </Badge>
                          <Badge 
                            className={cn(
                              "text-xs capitalize px-2 py-0.5",
                              (() => {
                                // Check if expired
                                const isExpired = job.expiresAt && new Date(job.expiresAt.toDate ? job.expiresAt.toDate() : job.expiresAt) < new Date();
                                const effectiveStatus = isExpired ? 'expired' : job.status;
                                
                                if (effectiveStatus === 'active') {
                                  return 'bg-green-500 text-white';
                                } else if (effectiveStatus === 'expired') {
                                  return theme === 'light' ? 'bg-orange-200 text-orange-800' : 'bg-orange-700 text-orange-300';
                                } else {
                                  return theme === 'light' ? 'bg-gray-200 text-gray-700' : 'bg-gray-700 text-gray-300';
                                }
                              })()
                            )}
                          >
                            {(() => {
                              const isExpired = job.expiresAt && new Date(job.expiresAt.toDate ? job.expiresAt.toDate() : job.expiresAt) < new Date();
                              return isExpired ? 'Expired' : (job.status || 'N/A');
                            })()}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PracticeDashboardPage;
